import { useMemo } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Playlist } from '@/types/playlist';
import { Song } from '@/types/song';
import { Music, Play, Clock, Tag, Building2, UsersRound, Volume2, BadgeCheck } from 'lucide-react';
import { cn } from '@/lib/utils';

interface PlaylistStatsProps {
  playlists: Playlist[];
}

// Müzik istatistiklerini hesaplama yardımcı fonksiyonu
const calculateMusicStats = (playlists: Playlist[]) => {
  // Tüm şarkıların listesi
  const allSongs: Song[] = [];
  let totalPlaylists = playlists.length;
  let publicPlaylists = 0;
  let assignedPlaylists = 0;
  
  // Tüm şarkıları topla ve diğer sayıları hesapla
  playlists.forEach(playlist => {
    if (playlist.songs) {
      allSongs.push(...playlist.songs);
    }
    
    if (playlist.isPublic) {
      publicPlaylists++;
    }
    
    if (playlist.assignedTo && playlist.assignedTo.length > 0) {
      assignedPlaylists++;
    }
  });
  
  // Toplam şarkı sayısı (tekrar edenler dahil)
  const totalSongs = allSongs.length;
  
  // Benzersiz şarkı sayısı (ID'ye göre)
  const uniqueSongs = [...new Set(allSongs.map(song => song._id))].length;
  
  // Toplam süre (saniye cinsinden)
  const totalDuration = allSongs.reduce((acc, song) => acc + (song.duration || 0), 0);
  
  // Benzersiz türler
  const uniqueGenres = [...new Set(allSongs.filter(song => song.genre).map(song => song.genre))];
  
  // Benzersiz sanatçılar
  const uniqueArtists = [...new Set(allSongs.filter(song => song.artist).map(song => song.artist))];
  
  return {
    totalPlaylists,
    publicPlaylists,
    assignedPlaylists,
    totalSongs,
    uniqueSongs,
    totalDuration,
    uniqueGenres: uniqueGenres.length,
    uniqueArtists: uniqueArtists.length
  };
};

// Süre formatını dönüştürme yardımcı fonksiyonu
const formatDuration = (durationInSeconds: number) => {
  const hours = Math.floor(durationInSeconds / 3600);
  const minutes = Math.floor((durationInSeconds % 3600) / 60);
  
  if (hours > 0) {
    return `${hours} saat ${minutes} dk`;
  }
  return `${minutes} dakika`;
};

export function PlaylistStats({ playlists }: PlaylistStatsProps) {
  const stats = useMemo(() => calculateMusicStats(playlists), [playlists]);
  
  const items = [
    {
      label: "Toplam Playlist",
      value: stats.totalPlaylists,
      icon: Music,
      color: "text-blue-500",
      bgColor: "bg-blue-500/10",
      detail: `${stats.publicPlaylists} açık playlist`
    },
    {
      label: "Atanmış Playlistler",
      value: stats.assignedPlaylists,
      icon: Building2,
      color: "text-indigo-500",
      bgColor: "bg-indigo-500/10",
      detail: `${Math.round((stats.assignedPlaylists / stats.totalPlaylists) * 100) || 0}% oranında atanmış`
    },
    {
      label: "Toplam Şarkı",
      value: stats.totalSongs,
      icon: Play,
      color: "text-green-500",
      bgColor: "bg-green-500/10",
      detail: `${stats.uniqueSongs} benzersiz şarkı`
    },
    {
      label: "Toplam Süre",
      value: formatDuration(stats.totalDuration),
      icon: Clock,
      color: "text-orange-500",
      bgColor: "bg-orange-500/10",
      detail: `Playlist başına ortalama ${formatDuration(Math.round(stats.totalDuration / stats.totalPlaylists))}`
    },
    {
      label: "Benzersiz Türler",
      value: stats.uniqueGenres,
      icon: Tag,
      color: "text-purple-500",
      bgColor: "bg-purple-500/10",
      detail: `${Math.round(stats.uniqueGenres / stats.totalSongs * 100) || 0}% tür çeşitliliği`
    },
    {
      label: "Benzersiz Sanatçılar",
      value: stats.uniqueArtists,
      icon: UsersRound,
      color: "text-pink-500",
      bgColor: "bg-pink-500/10",
      detail: `${Math.round(stats.uniqueArtists / stats.totalSongs * 100) || 0}% sanatçı çeşitliliği`
    },
    {
      label: "Açık Playlistler",
      value: stats.publicPlaylists,
      icon: Volume2,
      color: "text-cyan-500",
      bgColor: "bg-cyan-500/10",
      detail: `${Math.round((stats.publicPlaylists / stats.totalPlaylists) * 100) || 0}% açık playlist oranı`
    },
    {
      label: "Şarkılar/Playlist",
      value: Math.round(stats.totalSongs / stats.totalPlaylists) || 0,
      icon: BadgeCheck,
      color: "text-emerald-500",
      bgColor: "bg-emerald-500/10",
      detail: `Ortalama şarkı sayısı`
    }
  ];

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {items.slice(0, 4).map((item) => (
          <Card key={item.label} className="overflow-hidden border border-slate-200 shadow-sm hover:shadow-md transition-all duration-200 hover:border-slate-300 bg-white rounded-xl">
            <CardContent className="p-0">
              <div className="relative">
                <div className={`absolute top-0 right-0 w-32 h-32 opacity-5 transform translate-x-12 -translate-y-12 rounded-full ${item.bgColor} blur-2xl`}></div>
                <div className="p-6 relative">
                  <div className="flex justify-between items-start mb-4">
                    <div className="flex-1">
                      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">{item.label}</p>
                      <h3 className="text-3xl font-bold text-slate-900 tracking-tight">{item.value}</h3>
                    </div>
                    <div className={cn("p-3.5 rounded-xl shadow-sm", item.bgColor)}>
                      <item.icon className={cn("w-5 h-5", item.color)} strokeWidth={2.5} />
                    </div>
                  </div>
                  <div className="pt-3 border-t border-slate-100">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center text-xs text-slate-600">
                        <span className={`inline-block w-2 h-2 rounded-full mr-2 ${item.color.replace('text-', 'bg-')}`}></span>
                        <span className="font-medium">{item.detail}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {items.slice(4, 8).map((item) => (
          <Card key={item.label} className="overflow-hidden border border-slate-200 shadow-sm hover:shadow-md transition-all duration-200 hover:border-slate-300 bg-white rounded-xl">
            <CardContent className="p-0">
              <div className="relative">
                <div className={`absolute top-0 right-0 w-32 h-32 opacity-5 transform translate-x-12 -translate-y-12 rounded-full ${item.bgColor} blur-2xl`}></div>
                <div className="p-6 relative">
                  <div className="flex justify-between items-start mb-4">
                    <div className="flex-1">
                      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">{item.label}</p>
                      <h3 className="text-3xl font-bold text-slate-900 tracking-tight">{item.value}</h3>
                    </div>
                    <div className={cn("p-3.5 rounded-xl shadow-sm", item.bgColor)}>
                      <item.icon className={cn("w-5 h-5", item.color)} strokeWidth={2.5} />
                    </div>
                  </div>
                  <div className="pt-3 border-t border-slate-100">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center text-xs text-slate-600">
                        <span className={`inline-block w-2 h-2 rounded-full mr-2 ${item.color.replace('text-', 'bg-')}`}></span>
                        <span className="font-medium">{item.detail}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}

export default PlaylistStats;
